import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Typography,
  createStyles,
  Radio,
  DialogActions,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CachedIcon from '@material-ui/icons/Cached';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useTypedSelector, useAppDispatch } from 'combineReducers';
import {
  verifySufficientFunds,
  setDefaultPaymentMethod,
  resetCurrentSelected,
  setCurrentSelected,
  resetNewPaymentMethod,
  fetchPaymentMethods
} from 'actions/paymentMethods';
import {
  PaymentMethod,
  ConnectionsBillingPreviewPayload,
  VerifySufficientFundsPayload
} from 'interfaces/paymentMethods';
import { IConnectionBillingPreview } from 'interfaces/index';
import { fetchCompanyContact } from 'actions/companyContact';
import { Header } from 'components/PaymentConsent/Header';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';
import { AddNewPaymentMethod } from 'components/PaymentConsent/AddNewPaymentMethod';
import { SelectedConnectionsList } from './SelectedConnectionsList';
import { OverlayError } from './OverlayError';

import { usePrevious } from 'hooks/usePrevious';
import { showErrorPopUp } from 'helpers/overlayErrors';
import { cleanInsufficientFundsError } from 'actions/billingErrors';

interface Props {
  open: boolean;
  companyId: string;
  connections: IConnectionBillingPreview[];
  total: number;
  taxes: number;
  confirmMessage: string | JSX.Element | null;
  loading: boolean;
  handleClose(): void;
  renderConfirmButton(): JSX.Element;
  refreshPreview(): Promise<ConnectionsBillingPreviewPayload | undefined>;
}

const PaymentMethodModal: React.FC<Props> = ({
  open,
  handleClose,
  companyId,
  connections,
  total,
  taxes,
  renderConfirmButton,
  confirmMessage,
  refreshPreview,
  loading = false
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [isAddNewEnabled, setAddNewEnabled] = React.useState(false);

  const {
    paymentMethods,
    currentSelected,
    newPaymentMethod,
    isFetching: loadingPayment,
    hasPaymentMethods
  } = useTypedSelector(state => state.paymentMethods);
  const { companyContactData, isFetching: loadingContacts } = useTypedSelector(state => state.companyContact);
  const { billingSystemError, insufficientFundsError } = useTypedSelector(state => state.billingErrors);

  const sortPaymentMethods = (): PaymentMethod[] => {
    return [...paymentMethods].sort((first, second) =>
      first.isDefault
        ? -1
        : second.isDefault
        ? 1
        : new Date(second.createdDate).getTime() - new Date(first.createdDate).getTime()
    );
  };

  const defaultMethod = paymentMethods.find(method => method && method.isDefault);
  const anyFetching = loadingPayment || loadingContacts || loading;
  const isRecheckDisabled = !currentSelected.id || anyFetching;
  const prevPaymentMethodId = usePrevious(newPaymentMethod.id);
  const showPopUp = showErrorPopUp(billingSystemError.data);

  const requiresPaymentMethod = hasPaymentMethods === false;
  const hasPotentiallyFreeConnections = !!connections.filter(connection => connection.isPotentiallyFreeConnection)
    .length;

  const formatNameString = (name: string): string => {
    const formattedEncryptedPart = name
      .slice(0, -4)
      .replace(/\*/g, '·')
      .replace(/(.{4})/g, '$1 ');

    const numericPart = name.slice(-4);

    return formattedEncryptedPart.concat(numericPart);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddNewEnabled(false);
    dispatch(
      setCurrentSelected({
        id: event.target.value,
        isAuthorized: null
      })
    );
  };

  const handleAddNewEnable = () => {
    setAddNewEnabled(true);
  };

  const handleModalClose = () => {
    handleClose();
    setAddNewEnabled(false);
    dispatch(resetNewPaymentMethod());
    dispatch(resetCurrentSelected());
    dispatch(cleanInsufficientFundsError());
  };

  const handleRetry = async () => {
    dispatch(cleanInsufficientFundsError());
    const preview = await refreshPreview();

    if (preview) {
      dispatch(
        verifySufficientFunds({
          amount: preview?.total ?? total,
          companyId,
          paymentMethodId: currentSelected.id
        })
      );
    }
  };

  const renderPaymentMethod = (method: PaymentMethod) => {
    const isCurrentMethodSelected = method.id === currentSelected.id;
    const isNewPaymentMethod = method.id === newPaymentMethod.id;
    const isDefaultPaymentMethod = method.isDefault;
    const isInsufficientFunds = currentSelected.isAuthorized === false || insufficientFundsError.data;

    return (
      <div
        key={method.id}
        className={clsx(classes.methodContent, isCurrentMethodSelected && classes.currentSelected)}
        data-testid={`method-${method.id}`}
      >
        <div className={classes.radio}>
          <Radio onChange={handleChange} checked={isCurrentMethodSelected} value={method.id} color="primary" />
        </div>
        <div>
          <Typography variant="body2" color="textSecondary">
            <span>{method.cardType || method.type}</span>
            {isDefaultPaymentMethod && (
              <>
                <span> · </span>
                <span className={classes.default}>{t('subscriber:paymentMethodModal.preferred', 'Preferred')}</span>
              </>
            )}
            {isCurrentMethodSelected && isInsufficientFunds && (
              <>
                <span> · </span>
                <span className={classes.failed}>{t('subscriber:paymentMethodModal.failed', 'Failed')}</span>
              </>
            )}
            {isNewPaymentMethod && (
              <>
                <span> · </span>
                <span className={classes.new}>{t('subscriber:paymentMethodModal.new', 'New')}</span>
              </>
            )}
          </Typography>
          <Typography variant="body2" className={classes.cardName}>
            {formatNameString(method.name)}
          </Typography>
        </div>
      </div>
    );
  };

  const getHasSufficientFundsPayload = React.useCallback(
    async (total: number) => {
      const payload = await dispatch(
        verifySufficientFunds({
          companyId,
          amount: total,
          paymentMethodId: newPaymentMethod.id
        })
      );
      return (payload as unknown) as VerifySufficientFundsPayload;
    },
    [dispatch, newPaymentMethod, companyId]
  );

  const proceedNewPaymentMethod = React.useCallback(async () => {
    dispatch(cleanInsufficientFundsError());
    setAddNewEnabled(false);

    const preview = await refreshPreview();

    if (preview) {
      const hasFunds = await getHasSufficientFundsPayload(preview?.total ?? total);

      if (hasFunds && hasFunds.hasSufficientFunds) {
        dispatch(
          setDefaultPaymentMethod({
            companyId,
            paymentMethodId: newPaymentMethod.id
          })
        );
      } else {
        await dispatch(fetchPaymentMethods({ companyId }));
        dispatch(
          setCurrentSelected({
            isAuthorized: false,
            id: newPaymentMethod.id
          })
        );
      }
    }
  }, [dispatch, companyId, newPaymentMethod, getHasSufficientFundsPayload, refreshPreview, total]);

  React.useEffect(() => {
    if (companyId && open) {
      dispatch(fetchPaymentMethods({ companyId }));
    }
  }, [companyId, dispatch, open]);

  const verifyFundsOnLoad = React.useCallback(async () => {
    const preview = await refreshPreview();
    if (preview) {
      dispatch(
        verifySufficientFunds({
          amount: preview?.total,
          companyId
        })
      );
    }
  }, [companyId, dispatch, refreshPreview]);

  React.useEffect(() => {
    if (companyId && open) {
      verifyFundsOnLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  React.useEffect(() => {
    const isAddedNewPaymentMethod = newPaymentMethod.id && prevPaymentMethodId !== newPaymentMethod.id;

    if (isAddedNewPaymentMethod && open) {
      proceedNewPaymentMethod();
    }
  }, [newPaymentMethod, proceedNewPaymentMethod, prevPaymentMethodId, open]);

  React.useEffect(() => {
    const selectedHasSufficientFunds = Boolean(currentSelected.id && currentSelected.isAuthorized);
    const methodWasChanged = currentSelected.id !== defaultMethod?.id;

    if (selectedHasSufficientFunds && methodWasChanged && open) {
      dispatch(
        setDefaultPaymentMethod({
          companyId,
          paymentMethodId: currentSelected.id
        })
      );
    }
  }, [dispatch, currentSelected, companyId, defaultMethod, open]);

  React.useEffect(() => {
    if (isAddNewEnabled) {
      dispatch(resetCurrentSelected());
    }
  }, [dispatch, isAddNewEnabled, companyId]);

  React.useEffect(() => {
    if (companyContactData?.id !== Number(companyId) && open) {
      dispatch(fetchCompanyContact(companyId));
    }
  }, [dispatch, companyId, companyContactData, open]);

  return (
    <Dialog open={open} fullScreen={true} onClose={handleClose} id="paymentMethodDialog">
      <Header />
      {billingSystemError.data && !showPopUp ? (
        <div className={classes.errorContainer}>
          <OverlayError onClose={handleModalClose} />
        </div>
      ) : (
        <div className={classes.dialogContainer}>
          <DialogTitle className={classes.title} disableTypography>
            {t('subscriber:paymentMethodModal.modalTitle', 'Method(s) of Payment')}
          </DialogTitle>
          <DialogContent>
            <section className={classes.section}>
              <DialogContentText className={classes.subtitle}>
                {requiresPaymentMethod
                  ? t('subscriber:paymentMethodModal.modalActionRequiresPayment', 'Set up at least one payment method.')
                  : t(
                      'subscriber:paymentMethodModal.modalAction',
                      'Select an existing payment method or add a new one.'
                    )}
              </DialogContentText>
              <LoadingOverlay isLoading={!!hasPaymentMethods && loadingPayment}>
                <div>{sortPaymentMethods().map(method => renderPaymentMethod(method))}</div>
              </LoadingOverlay>
              <div className={clsx(classes.methodContent, isAddNewEnabled && classes.currentSelected)}>
                <Radio
                  color="primary"
                  className={classes.radio}
                  value={isAddNewEnabled}
                  onChange={handleAddNewEnable}
                  checked={isAddNewEnabled}
                  inputProps={{ 'aria-label': t('subscriber:paymentMethodModal.addNewRadio', 'Add new radio') }}
                />
                <Typography variant="body2" className={classes.cardName}>
                  {t('subscriber:paymentMethodModal.addNew', 'Add new payment method')}
                </Typography>
              </div>
            </section>
            {isAddNewEnabled ? (
              <AddNewPaymentMethod
                contactData={companyContactData}
                loading={loadingContacts}
                aria-label={t('subscriber:paymentMethodModal.addPaymentComponent', 'Add payment component')}
              />
            ) : (
              <section className={classes.section}>
                <div className={classes.retryContainer}>
                  <DialogContentText className={classes.subtitle}>
                    {t('subscriber:paymentMethodModal.orderDetails', 'Order Details')}
                  </DialogContentText>
                  <Button color="primary" onClick={handleRetry} startIcon={<CachedIcon />} disabled={isRecheckDisabled}>
                    {t('subscriber:paymentMethodModal.reTryPaymentBtn', 'RETRY')}
                  </Button>
                </div>
                {currentSelected.isAuthorized && !insufficientFundsError.data && hasPaymentMethods && (
                  <div className={classes.successNotification}>
                    <div className={classes.successNotificationTitle}>
                      <CheckCircleIcon className={classes.iconSuccess} />
                      <Typography variant="subtitle1" className={classes.successInfo}>
                        {t(
                          'subscriber:paymentMethodModal.notifySuccess',
                          'Selected payment method validated. This will now be saved as the preferred payment method on your account.'
                        )}
                      </Typography>
                    </div>
                  </div>
                )}
                <DialogContentText className={classes.descriptionInfo}>
                  {t('subscriber:paymentMethodModal.connectionsList', 'List of current connection requests:')}
                </DialogContentText>
                <LoadingOverlay isLoading={loading}>
                  <SelectedConnectionsList connections={connections} taxes={taxes} total={total} />
                </LoadingOverlay>
                {hasPotentiallyFreeConnections && (
                  <Typography className={classes.confirmMessage} variant="body2" color="textSecondary">
                    {t(
                      'subscriber:paymentMethodModal.freeConnectionsDisclaimer',
                      'You may not be charged if connections already exist or you have reached the limit of billable connections for your subscription term.'
                    )}
                  </Typography>
                )}
                {confirmMessage && (
                  <Typography className={classes.confirmMessage} variant="body2" color="textSecondary">
                    {confirmMessage}
                  </Typography>
                )}
              </section>
            )}
            <DialogActions className={classes.actionsContainer}>
              {renderConfirmButton()}
              <Button color="primary" onClick={handleModalClose}>
                {t('subscriber:paymentMethodModal.cancelModalBtn', 'Cancel')}
              </Button>
            </DialogActions>
          </DialogContent>
        </div>
      )}
    </Dialog>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    dialogContainer: {
      width: '768px',
      margin: 'auto'
    },
    title: {
      letterSpacing: 'var(--character-spacing-0)',
      font: 'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-32) var(--font-family-roboto)'
    },
    methodContent: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '14px',
      height: '70px',
      border: '1px solid var(--on-surface-outline-000000-12-)',
      borderRadius: '4px'
    },
    currentSelected: {
      border: '1px solid var(--primary-blue-six)'
    },
    subtitle: {
      color: 'var(--on-surface-medium-emphasis-000000-87-)',
      letterSpacing: 'var(--character-spacing-0-4)',
      font:
        'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-20)/var(--line-spacing-24) var(--font-family-roboto)'
    },
    descriptionInfo: {
      color: 'var(--on-surface-medium-emphasis-000000-87-)',
      letterSpacing: 'var(--character-spacing-0-5)',
      font:
        'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-14)/var(--line-spacing-20) var(--font-family-roboto)'
    },
    successInfo: {
      color: 'var(--dark-green-status-success)'
    },
    radio: {
      margin: '15px'
    },
    section: {
      marginBottom: '10px'
    },
    cardName: {
      fontWeight: 500,
      margin: '5px 0 0 0'
    },
    failed: {
      color: 'var(--status-error)'
    },
    new: {
      color: 'var(--dark-green-status-success)'
    },
    default: {
      color: 'var(--on-surface-medium-emphasis-000000-60-)'
    },
    iconSuccess: {
      marginRight: '10px',
      color: 'var(--dark-green-status-success)'
    },
    successNotification: {
      marginBottom: '16px'
    },
    successNotificationTitle: {
      display: 'flex',
      marginBottom: '6px',
      alignItems: 'center'
    },
    confirmMessage: {
      marginTop: '12px'
    },
    actionsContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      padding: '0 0 20px'
    },
    retryContainer: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    errorContainer: {
      margin: '20px auto auto',
      width: '768px'
    }
  })
);

export default PaymentMethodModal;
